import { img2 } from "../../components/images";
import jsPDF from "jspdf";
import dayjs from "dayjs";

/**
 * Generates a PDF displaying all blocks (question lists) for a department.
 *
 * @param {Object} params
 * @param {Array} params.blockIdsByDept - Array of block metadata for the department
 * @param {Object} params.allBlockItems - Object keyed by blkid containing arrays of question items
 * @param {string} params.departmentName - Name of the department
 * @param {string|number} params.termNumber - Term number
 */
export default function displayBlockPDF({
	blockIdsByDept,
	allBlockItems,
	departmentName,
	termNumber,
}) {
	const doc = new jsPDF();
	console.log("all block items in pdf generator:", allBlockItems);

	// --- PAGE SETUP & CONSTANTS ---
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();
	const margin = 15;
	const bottomMargin = 25;
	const lineHeight = 6;
	const questionIndent = 10;
	const maxTextWidth = pageWidth - margin * 2 - questionIndent;

	let currentY = 50;

	// --- HELPER: Check if we need a new page ---
	const checkNewPage = (neededHeight = lineHeight) => {
		if (currentY + neededHeight > pageHeight - bottomMargin) {
			doc.addPage();
			currentY = margin + 10;
			return true;
		}
		return false;
	};

	// --- HELPER: Draw page header ---
	const drawPageHeader = () => {
		doc.addImage(img2, "PNG", pageWidth - margin - 25, 10, 25, 25);
		doc.setFontSize(16);
		doc.setFont(undefined, "bold");
		doc.text("SPOT Question Blocks", pageWidth / 2, 25, { align: "center" });
		doc.setFontSize(10);
		doc.setFont(undefined, "normal");
		doc.text(`Department: ${departmentName || "N/A"}`, margin, 35);
		doc.text(`Term: ${termNumber || "N/A"}`, margin, 41);
		doc.text(`Generated: ${dayjs().format("DD/MMM/YYYY")}`, pageWidth - margin - 45, 40);
	};

	// --- HELPER: Wrap text to fit within width ---
	const wrapText = (text, maxWidth) => {
		if (!text) return [""];
		const words = text.split(" ");
		const lines = [];
		let currentLine = "";

		words.forEach((word) => {
			const testLine = currentLine ? `${currentLine} ${word}` : word;
			const testWidth = doc.getTextWidth(testLine);
			if (testWidth > maxWidth && currentLine) {
				lines.push(currentLine);
				currentLine = word;
			} else {
				currentLine = testLine;
			}
		});
		if (currentLine) lines.push(currentLine);
		return lines;
	};

	// --- DRAW FIRST PAGE HEADER ---
	drawPageHeader();

	// --- FILTER AND SORT BLOCKS ---
	// Filter out EXCL and sort by blkid
	const validBlocks = (blockIdsByDept || [])
		.filter((block) => block.blkid && block.blkid !== "EXCL")
		.sort((a, b) => a.blkid.localeCompare(b.blkid));

	if (validBlocks.length === 0) {
		currentY = 60;
		doc.setFontSize(12);
		doc.text("No blocks available for this department.", margin, currentY);
		doc.save(`SPOT_Blocks_${departmentName || "Unknown"}_${termNumber || ""}.pdf`);
		return;
	}

	// --- RENDER EACH BLOCK ---
	validBlocks.forEach((block, blockIndex) => {
		const blkid = block.blkid;
		const items = allBlockItems?.[blkid] || [];

		// Calculate estimated height for block header + first question
		const estimatedBlockHeight = lineHeight * 3 + (items.length > 0 ? lineHeight * 2 : 0);

		// Check if we need a new page before starting a new block
		if (checkNewPage(estimatedBlockHeight)) {
			// Add a small header on continuation pages
			doc.setFontSize(8);
			doc.setTextColor(100);
			doc.text(`SPOT Question Blocks - ${departmentName || ""}`, margin, margin);
			doc.setTextColor(0);
			currentY = margin + 10;
		}

		// Add spacing between blocks (except first one)
		if (blockIndex > 0) {
			currentY += 8;
		}

		// --- BLOCK HEADER ---
		doc.setFillColor(39, 93, 56); // UAlberta green
		doc.rect(margin, currentY, pageWidth - margin * 2, 8, "F");
		doc.setTextColor(255);
		doc.setFontSize(11);
		doc.setFont(undefined, "bold");

		// Block title with metadata
		const sizeLabel = block.csize === "SMALL" ? "(Small Classes Only)" : "";
		const roleLabel =
			block.inst_role === "TA" ? "[TA ONLY]" : block.inst_role === "PI" ? "[NON-TA]" : "";
		const blockTitle = `${blkid} ${sizeLabel} ${roleLabel}`.trim();
		doc.text(blockTitle, margin + 3, currentY + 5.5);

		doc.setTextColor(0);
		doc.setFont(undefined, "normal");
		currentY += 10;

		// --- BLOCK METADATA ---
		doc.setFontSize(9);
		doc.setTextColor(80);
		const ctypeText = block.ctype ? `Class Type: ${block.ctype}` : "";

		const metaLine = ctypeText;
		if (metaLine) {
			doc.text(metaLine, margin + 2, currentY + 3);
			currentY += lineHeight;
		}
		doc.setTextColor(0);

		// --- QUESTIONS ---
		if (items.length === 0) {
			doc.setFontSize(9);
			doc.setTextColor(120);
			doc.text("No questions defined for this block.", margin + questionIndent, currentY + 4);
			doc.setTextColor(0);
			currentY += lineHeight;
		} else {
			// Sort items by sequence if available
			const sortedItems = [...items].sort((a, b) => (a.seq || 0) - (b.seq || 0));

			sortedItems.forEach((item, idx) => {
				const questionNum = idx + 1;
				const questionText = item.text || item.itext || `Item ${item.itemno || "?"}`;

				// Wrap question text
				const wrappedLines = wrapText(`${questionNum}. ${questionText}`, maxTextWidth);
				const questionHeight = wrappedLines.length * lineHeight;

				// Check for page break
				if (checkNewPage(questionHeight + 2)) {
					doc.setFontSize(8);
					doc.setTextColor(100);
					doc.text(`${blkid} (continued)`, margin, margin);
					doc.setTextColor(0);
					currentY = margin + 8;
				}

				doc.setFontSize(9);
				wrappedLines.forEach((line, lineIdx) => {
					doc.text(line, margin + questionIndent, currentY + 4);
					currentY += lineHeight - 1;
				});
				currentY += 2; // Small gap between questions
			});
		}

		// Draw a subtle separator line
		doc.setDrawColor(200);
		doc.line(margin, currentY + 2, pageWidth - margin, currentY + 2);
		currentY += 4;
	});

	// --- ADD PAGE NUMBERS ---
	const totalPages = doc.internal.getNumberOfPages();
	for (let i = 1; i <= totalPages; i++) {
		doc.setPage(i);
		doc.setFontSize(8);
		doc.setTextColor(120);
		doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: "center" });
	}

	// --- SAVE PDF ---
	const fileName = `SPOT_Blocks_${(departmentName || "Dept").replace(/[^a-zA-Z0-9]/g, "_")}_${termNumber || ""}.pdf`;
	doc.save(fileName);
}
